import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Download, FileText, Lock, X } from "lucide-react";
import { BRIEF_SECTIONS } from "../brief/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = (() => {
  const map = {};
  BRIEF_SECTIONS.forEach((s) => s.fields.forEach((f) => (map[f.id] = { label: f.label, section: s.title })));
  return map;
})();

const fmt = (v) => {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.map((x) => (typeof x === "object" ? `${x.name || "—"} (${x.origin || "—"})${x.notes ? ` — ${x.notes}` : ""}` : x)).join(", ");
  return String(v ?? "");
};

export default function BriefInbox() {
  const [token, setToken] = useState(sessionStorage.getItem("brief-token") || "");
  const [authed, setAuthed] = useState(false);
  const [briefs, setBriefs] = useState(null);
  const [error, setError] = useState("");
  const [openRef, setOpenRef] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async (t) => {
    setError("");
    try {
      const res = await fetch(`${API}/briefs`, { headers: { "X-Admin-Token": t } });
      if (!res.ok) throw new Error("rejected");
      const list = await res.json();
      sessionStorage.setItem("brief-token", t);
      setBriefs(list);
      setAuthed(true);
    } catch {
      setError("Access token not recognised. Check the ADMIN_ACCESS_TOKEN value.");
      setAuthed(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, []); // eslint-disable-line

  useEffect(() => {
    document.title = "Brief Inbox — ENEXUR India Market Intelligence™";
  }, []);

  const openBrief = async (ref) => {
    if (openRef === ref) {
      setOpenRef(null);
      setDetail(null);
      return;
    }
    setOpenRef(ref);
    setDetail("loading");
    const res = await fetch(`${API}/briefs/${ref}`, { headers: { "X-Admin-Token": token } });
    if (res.ok) setDetail(await res.json());
    else setDetail(null);
  };

  const download = async (ref, index, name) => {
    const res = await fetch(`${API}/briefs/${ref}/attachment/${index}`, {
      headers: { "X-Admin-Token": token },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6">
        <div className="w-full max-w-md border border-gold/25 bg-abyss/70 p-10">
          <Lock size={20} className="text-gold" aria-hidden="true" />
          <h1 className="mt-6 font-display text-3xl text-ivory">Brief Inbox</h1>
          <p className="mt-3 text-sm leading-relaxed text-mist">
            Private area for ENEXUR. Enter the admin access token to review Client
            Intelligence Briefs and download attachments.
          </p>
          <input
            type="password"
            className="field mt-8"
            placeholder="Admin access token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && token && load(token)}
            data-testid="inbox-token-input"
          />
          {error && <p className="mt-3 text-xs text-terra">{error}</p>}
          <button onClick={() => token && load(token)} className="btn-gold mt-6 w-full" data-testid="inbox-unlock-button">
            Unlock inbox
          </button>
          <Link
            to="/india-market-intelligence"
            className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-mist hover:text-gold"
          >
            <ArrowLeft size={12} aria-hidden="true" />
            Product page
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen">
      <header className="border-b border-gold/15 pt-[76px]">
        <div className="wrap flex items-end justify-between pb-10 pt-12">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
              ENEXUR — private
            </p>
            <h1 className="mt-4 font-display text-4xl text-ivory">Client Intelligence Briefs</h1>
          </div>
          <button
            onClick={() => { sessionStorage.removeItem("brief-token"); setAuthed(false); setBriefs(null); setToken(""); }}
            className="border border-gold/30 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-mist hover:text-gold"
            data-testid="inbox-lock-button"
          >
            Lock
          </button>
        </div>
      </header>

      <section className="py-12">
        <div className="wrap space-y-4" data-testid="inbox-list">
          {briefs.length === 0 && (
            <p className="border border-gold/15 bg-abyss/50 p-8 text-sm text-mist">
              No briefs have been submitted yet.
            </p>
          )}
          {briefs.map((b) => (
            <div key={b.reference} className="border border-gold/15 bg-abyss/50">
              <button
                onClick={() => openBrief(b.reference)}
                className="flex w-full flex-wrap items-center gap-x-8 gap-y-2 p-6 text-left transition-colors hover:bg-hull/40"
                data-testid={`inbox-row-${b.reference}`}
              >
                <span className="font-mono text-[11px] tracking-[0.18em] text-gold">{b.reference}</span>
                <span className="font-display text-lg text-ivory">{b.company_name || "—"}</span>
                <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.16em] text-mist/70">
                  {b.submitted_at}
                </span>
                {b.files?.length > 0 && (
                  <span className="flex items-center gap-2 font-mono text-[10px] text-gold">
                    <FileText size={12} aria-hidden="true" />
                    {b.files.length} file{b.files.length > 1 ? "s" : ""}
                  </span>
                )}
              </button>

              {openRef === b.reference && detail && (
                <div className="border-t border-gold/12 p-6 md:p-8">
                  {detail === "loading" && <p className="text-sm text-mist">Loading…</p>}
                  {detail !== "loading" && (
                    <>
                      <dl className="grid gap-x-10 gap-y-4 md:grid-cols-2">
                        {Object.entries(detail.data ?? {})
                          .filter(([k]) => !k.startsWith("files_") && k !== "submitted_at")
                          .map(([k, v]) => (
                            <div key={k} className="border-b border-gold/8 pb-3">
                              <dt className="font-mono text-[9px] uppercase tracking-[0.2em] text-mist/60">
                                {LABELS[k]?.label ?? k}
                                {LABELS[k]?.section ? ` — ${LABELS[k].section}` : ""}
                              </dt>
                              <dd className="mt-1.5 text-sm leading-relaxed text-ivory/85">{fmt(v) || "—"}</dd>
                            </div>
                          ))}
                      </dl>
                      {detail.files?.length > 0 && (
                        <div className="mt-8">
                          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">Attachments</p>
                          <ul className="mt-4 space-y-2">
                            {detail.files.map((f, i) => (
                              <li key={i} className="flex flex-wrap items-center gap-4 border border-gold/15 bg-ink px-4 py-3">
                                <FileText size={14} className="shrink-0 text-gold" aria-hidden="true" />
                                <span className="text-sm text-ivory/85">{f.name}</span>
                                <span className="font-mono text-[10px] text-mist/60">{(f.size / 1024).toFixed(0)} KB</span>
                                <button
                                  onClick={() => download(detail.reference, i, f.name)}
                                  className="ml-auto flex items-center gap-2 border border-gold/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-gold transition-colors hover:bg-gold hover:text-ink"
                                  data-testid={`inbox-download-${detail.reference}-${i}`}
                                >
                                  <Download size={11} aria-hidden="true" />
                                  Download
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
